import React, { useState, useEffect } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function MessageThread({ threadId, student, theme }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!student || !student.id || !threadId) {
      setError("Invalid student or thread data.");
      setLoading(false);
      return;
    }

    const fetchMessages = async () => {
      try {
       
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/messages/thread/${threadId}/${student.id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to fetch messages");
        setMessages(data);
        setLoading(false);
      } catch (err) {
        console.error("Fetch messages error:", err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchMessages();
  }, [threadId, student]);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/30 backdrop-blur-xl shadow-lg sm:shadow-2xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-100/80 p-2 rounded-lg">
            <MessageSquare 
              className="w-6 h-6 sm:w-7 sm:h-7" 
              style={{ color: theme?.primary || "#2563eb" }} 
            />
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
            Conversation
          </h2>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-3 rounded-lg mb-6 bg-red-100/80 text-red-700 border border-red-300/50 text-sm sm:text-base"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex justify-center py-8 sm:py-12">
            <Loader2 
              className="w-7 h-7 sm:w-8 sm:h-8 animate-spin" 
              style={{ color: theme?.primary || "#2563eb" }} 
            />
          </div>
        ) : messages.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="border border-dashed border-gray-200/50 rounded-xl p-6 text-center bg-gray-50/20"
          >
            <p className="text-gray-500 text-base sm:text-lg">No messages in this thread</p>
          </motion.div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`p-3 sm:p-4 rounded-xl ${
                  msg.message_from === student.id
                    ? "bg-blue-100/80 text-blue-900 ml-0 sm:ml-6 md:ml-8"
                    : "bg-gray-100/80 text-gray-900 mr-0 sm:mr-6 md:mr-8"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-1 gap-1 sm:gap-0">
                  <span className="font-semibold text-sm sm:text-base">
                    {msg.sender_firstName} {msg.sender_lastName}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    {new Date(msg.created_at).toLocaleString([], {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="text-sm sm:text-base mt-1 sm:mt-0">{msg.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}